import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import RevealProvider from '../components/RevealProvider';
import { getPost, POSTS, formatDate } from '../data/posts';

function renderBlock(block, i){
  switch (block.type){
    case 'h2':
      return <h2 key={i} className="post-h2">{block.text}</h2>;
    case 'h3':
      return <h3 key={i} className="post-h3">{block.text}</h3>;
    case 'paragraph':
      return <p key={i} className="post-p">{block.text}</p>;
    case 'list':
      return (
        <ul key={i} className="post-list">
          {block.items.map((it, j) => <li key={j}>{it}</li>)}
        </ul>
      );
    case 'quote':
      return <blockquote key={i} className="post-quote">{block.text}</blockquote>;
    case 'note':
      return <div key={i} className="post-note"><strong>Our recommendation</strong>{block.text}</div>;
    case 'table':
      return (
        <div key={i} className="post-table-wrap">
          <table className="post-table">
            <thead><tr>{block.header.map((h, j) => <th key={j}>{h}</th>)}</tr></thead>
            <tbody>{block.rows.map((r, j) => (
              <tr key={j}>{r.map((c, k) => <td key={k}>{c}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      );
    case 'cta':
      return (
        <div key={i} className="post-cta">
          <p>{block.text}</p>
          <Link to="/#enquiry" className="btn-primary">Request a consultation</Link>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogPost(){
  const { slug } = useParams();
  const post = getPost(slug);
  const nav = useNavigate();

  if (!post){
    return (
      <div className="notfound">
        <Helmet><title>Post not found — Nomad Compass</title></Helmet>
        <div>
          <h1><em>Not here.</em></h1>
          <p>That blog post doesn’t exist. Browse the full journal below.</p>
          <button className="btn-primary" onClick={() => nav('/blog')}>View the journal</button>
        </div>
      </div>
    );
  }

  const related = POSTS.filter(p => p.slug !== post.slug).slice(0, 2);
  const url = `https://nomadcompasshub.com/blog/${post.slug}`;

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "datePublished": post.publishedAt,
    "dateModified": post.publishedAt,
    "author": { "@type": "Organization", "name": "Nomad Compass" },
    "publisher": { "@type": "Organization", "name": "Nomad Compass", "url": "https://nomadcompasshub.com" },
    "mainEntityOfPage": { "@type": "WebPage", "@id": url },
    "image": post.heroImage,
    "url": url
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://nomadcompasshub.com/" },
      { "@type": "ListItem", "position": 2, "name": "Journal", "item": "https://nomadcompasshub.com/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": url },
    ]
  };

  return (
    <RevealProvider>
      <Helmet>
        <title>{`${post.title} — Nomad Compass Journal`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={post.heroImage} />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <TopBar variant="page" />

      <article className="post">
        <header className="post-header">
          <div className="post-header-inner">
            <div className="post-crumbs">
              <Link to="/blog">Journal</Link>
              <span>/</span>
              <span>{post.category}</span>
            </div>
            <h1 className="post-title">{post.title}</h1>
            {post.deck && <p className="post-deck">{post.deck}</p>}
            <div className="post-byline">
              <span>By {post.author}</span>
              <span>·</span>
              <span>{formatDate(post.publishedAt)}</span>
              <span>·</span>
              <span>{post.readMinutes} minute read</span>
            </div>
          </div>
        </header>

        <div className="post-hero-image reveal">
          <img src={post.heroImage} alt={post.title} />
        </div>

        <div className="post-body">
          {post.content.map(renderBlock)}
        </div>
      </article>

      {related.length > 0 && (
        <section className="post-related">
          <div className="post-related-inner">
            <h3>Keep reading</h3>
            <div className="post-related-grid">
              {related.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="blog-card reveal" data-testid={`related-${p.slug}`}>
                  <div className="blog-card-image"><img src={p.heroImage} alt={p.title} loading="lazy" /></div>
                  <div className="blog-card-body">
                    <div className="blog-meta">
                      <span className="blog-pill">{p.category}</span>
                      <span>{p.readMinutes} min</span>
                    </div>
                    <h4>{p.title}</h4>
                    <p>{p.excerpt}</p>
                    <span className="blog-readmore">Read →</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </RevealProvider>
  );
}
